import React, { useEffect, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { BRAND, PAGE, SALESCODE_KEY } from "../common/Constants";
import AuthenticationClient from "../shared/clients/AuthenticationClient";
import { logMsg } from "../common/Logger";
import { is401, is500 } from "../errors/ErrorConstants";
import App, { hideOrShow } from "../apps/App";
import Loading from "../shared/loading/Loading";
import ErrorScreen from "../errors/ErrorScreen";
import { getBackendScopes } from "./authConfig";
import { isLikelyJwt } from "./tokenShape";
import { setMsalAccount } from "./msalAccount";

const readParam = (sessionKey, urlKey = sessionKey) =>
  new URLSearchParams(window.location.search).get(urlKey) ||
  sessionStorage.getItem(sessionKey);

const buildAuthRequest = () => {
  const request = {};
  const salesCode = readParam(SALESCODE_KEY, "salesCode");
  const page = readParam(PAGE);
  const brand = readParam(BRAND);
  if (salesCode) request.salesCode = salesCode;
  if (page) request.page = page;
  if (brand) request.brand = brand;
  return { request, salesCode };
};

const persistSession = (userDetails, page, brand) => {
  if (userDetails.salesCode)
    sessionStorage.setItem(SALESCODE_KEY, userDetails.salesCode);
  if (page) sessionStorage.setItem(PAGE, page);
  if (brand) sessionStorage.setItem(BRAND, brand);
  if (userDetails.market) sessionStorage.setItem("market", userDetails.market);
};

const acquireToken = async (instance, account) => {
  const request = { scopes: getBackendScopes(), account };
  try {
    return await instance.acquireTokenSilent(request);
  } catch {
    await instance.acquireTokenRedirect(request);
    return null;
  }
};

const mapErrorToState = (error) => {
  if (is500(error)) return { loading: false, error: "PAGE_ERROR" };
  if (is401(error)) return { loading: false, error: "ACCESS_DENIED" };
  return { loading: false, error: "AUTH_FAILED" };
};

const MsalAuthWrapper = () => {
  const { instance, accounts, inProgress } = useMsal();
  const [authState, setAuthState] = useState({
    loading: true,
    token: null,
    userDetails: null,
    error: null,
  });

  useEffect(() => {
    if (inProgress !== InteractionStatus.None) return;

    let cancelled = false;

    hideOrShow(true);

    const runAuth = async () => {
      try {
        if (accounts.length === 0) {
          // Request backend scope consent on the initial login redirect
          // so the first acquireTokenSilent after sign-in returns a
          // backend-scoped JWT (not a Graph token).
          await instance.loginRedirect({ scopes: getBackendScopes() });
          return;
        }

        // Register the account *before* the first await so the axios
        // interceptor has it available for the /authorize call itself.
        setMsalAccount(accounts[0]);

        const response = await acquireToken(instance, accounts[0]);
        if (!response) return;

        const token = response.accessToken;
        if (!isLikelyJwt(token)) {
          throw new Error("AUTH_FAILED");
        }

        if (cancelled) return;

        const { request: authRequest } = buildAuthRequest();
        const userDetails = await AuthenticationClient.status(authRequest);

        if (cancelled) return;

        persistSession(userDetails, readParam(PAGE), readParam(BRAND));
        setAuthState({
          loading: false,
          token,
          userDetails: { ...userDetails, brand: userDetails.dealerBrand },
          error: null,
        });
      } catch (error) {
        if (cancelled) return;
        logMsg(error);
        setAuthState(mapErrorToState(error));
      } finally {
        if (!cancelled) hideOrShow(false);
      }
    };

    runAuth();

    return () => {
      cancelled = true;
    };
  }, [instance, accounts, inProgress]);

  if (authState.loading || inProgress !== InteractionStatus.None) {
    return <Loading />;
  }

  if (authState.error) {
    if (authState.error === "PAGE_ERROR") {
      throw new Error("PAGE_ERROR");
    }
    if (authState.error === "ACCESS_DENIED") {
      return (
        <ErrorScreen
          errorMessageKey="AuthAccessDeniedScreen.errorMessage"
          buttonTextKey="AuthAccessDeniedScreen.buttonText"
          titleKey="AuthAccessDeniedScreen.header"
        />
      );
    }
    return (
      <div style={{ padding: 20, textAlign: "center" }}>
        <p>Authentication failed. Please try again.</p>
      </div>
    );
  }

  return <App token={authState.token} userDetails={authState.userDetails} />;
};

export default MsalAuthWrapper;
